import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { Briefcase, Plus, Trash2, Loader2, Save, X as XIcon, RotateCcw } from "lucide-react";

const INPUT = "w-full bg-white border border-[var(--border)] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-[var(--ink)]";

export default function FirmServices() {
  const { state } = useAuth();
  const isAdmin = ["owner", "manager", "admin"].includes(state?.user?.role);
  const [rows, setRows] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);  // null | partial FirmServiceIn
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});

  const reload = async () => {
    try {
      const r = await axios.get(`${API_BASE}/firm-services?include_inactive=${includeInactive}`);
      setRows(r.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includeInactive]);

  const startAdd = () => setDraft({
    name: "", code: "", description: "", default_fee: "", sort_order: rows.length + 1,
  });
  const cancelAdd = () => setDraft(null);

  const create = async () => {
    if (!draft?.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: draft.name.trim(),
        code: draft.code?.trim() || null,
        description: draft.description?.trim() || null,
        default_fee: draft.default_fee !== "" ? Number(draft.default_fee) : null,
        sort_order: Number(draft.sort_order) || 0,
        active: true,
      };
      await axios.post(`${API_BASE}/firm-services`, payload);
      toast.success(`Added ${payload.name}`);
      setDraft(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const startEdit = (row) => {
    setEditingId(row.service_id);
    setEdit({
      name: row.name || "",
      code: row.code || "",
      description: row.description || "",
      default_fee: row.default_fee ?? "",
      sort_order: row.sort_order ?? 0,
      active: row.active !== false,
    });
  };
  const cancelEdit = () => { setEditingId(null); setEdit({}); };

  const saveEdit = async () => {
    if (!edit.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: edit.name.trim(),
        code: edit.code?.trim() || null,
        description: edit.description?.trim() || null,
        default_fee: edit.default_fee !== "" ? Number(edit.default_fee) : null,
        sort_order: Number(edit.sort_order) || 0,
        active: !!edit.active,
      };
      await axios.patch(`${API_BASE}/firm-services/${editingId}`, payload);
      toast.success("Saved");
      cancelEdit();
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const deactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"? Existing leads/clients keep this tag.`)) return;
    try {
      await axios.delete(`${API_BASE}/firm-services/${row.service_id}`);
      toast.success("Deactivated");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reactivate = async (row) => {
    try {
      await axios.patch(`${API_BASE}/firm-services/${row.service_id}`, { active: true });
      toast.success("Reactivated");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="px-10 py-12 max-w-[1200px]" data-testid="firm-services-page">
      <div className="mb-8">
        <div className="overline mb-2">Settings</div>
        <h1 className="font-heading text-3xl flex items-center gap-2" style={{ fontWeight: 500 }}>
          <Briefcase size={24}/> Services catalogue
        </h1>
        <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
          The services your firm offers. Tag these to leads and clients so the team
          immediately knows what work is in scope. Each service can carry a default fee
          and link to an estimate template.
        </p>
      </div>

      <div className="card p-5 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <label className="text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive}
                   onChange={(e) => setIncludeInactive(e.target.checked)}
                   data-testid="firm-services-show-inactive"/>
            Show deactivated
          </label>
          {isAdmin && !draft && (
            <button onClick={startAdd} className="btn-primary" data-testid="firm-services-add-btn">
              <Plus size={14}/> Add service
            </button>
          )}
        </div>

        {draft && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--subtle)] p-3 space-y-2" data-testid="firm-services-new-row">
            <div className="grid grid-cols-12 gap-2">
              <input className={`${INPUT} col-span-4`} placeholder="Service name (e.g. Individual Tax Return)"
                     value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                     data-testid="firm-services-new-name"/>
              <input className={`${INPUT} col-span-2`} placeholder="CODE" maxLength={12}
                     value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                     data-testid="firm-services-new-code"/>
              <input className={`${INPUT} col-span-2`} placeholder="Default fee" type="number" min="0" step="0.01"
                     value={draft.default_fee} onChange={(e) => setDraft((d) => ({ ...d, default_fee: e.target.value }))}
                     data-testid="firm-services-new-fee"/>
              <input className={`${INPUT} col-span-1`} placeholder="#" type="number"
                     value={draft.sort_order} onChange={(e) => setDraft((d) => ({ ...d, sort_order: e.target.value }))}
                     data-testid="firm-services-new-order" title="Sort order"/>
              <div className="col-span-3 flex items-center justify-end gap-1.5">
                <button onClick={create} disabled={busy} className="btn-primary text-[12px] py-1.5" data-testid="firm-services-new-save">
                  {busy ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save
                </button>
                <button onClick={cancelAdd} className="btn-secondary text-[12px] py-1.5" data-testid="firm-services-new-cancel">
                  <XIcon size={12}/>
                </button>
              </div>
            </div>
            <input className={INPUT} placeholder="Optional description (shown on hover)"
                   value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}/>
          </div>
        )}

        <div className="overflow-hidden border border-[var(--border)] rounded-md">
          <table className="w-full text-[13px]">
            <thead className="bg-[var(--subtle)] text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
              <tr>
                <th className="text-left px-3 py-2 w-12">#</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 w-24">Code</th>
                <th className="text-right px-3 py-2 w-28">Default fee</th>
                <th className="text-right px-3 py-2 w-24">Status</th>
                {isAdmin && <th className="text-right px-3 py-2 w-32">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-[var(--ink-3)] py-8" data-testid="firm-services-empty">
                  No services yet — click Add service to start your catalogue.
                </td></tr>
              )}
              {rows.map((r) => editingId === r.service_id ? (
                <tr key={r.service_id} className="bg-[var(--subtle)]" data-testid={`firm-services-edit-row-${r.service_id}`}>
                  <td className="px-3 py-2">
                    <input className={INPUT} type="number" value={edit.sort_order}
                           onChange={(e) => setEdit((s) => ({ ...s, sort_order: e.target.value }))}/>
                  </td>
                  <td className="px-3 py-2 space-y-1">
                    <input className={INPUT} value={edit.name}
                           onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))}
                           data-testid={`firm-services-edit-name-${r.service_id}`}/>
                    <input className={`${INPUT} text-[11px]`} placeholder="description"
                           value={edit.description}
                           onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))}/>
                  </td>
                  <td className="px-3 py-2">
                    <input className={INPUT} maxLength={12} value={edit.code}
                           onChange={(e) => setEdit((s) => ({ ...s, code: e.target.value.toUpperCase() }))}/>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input className={`${INPUT} text-right`} type="number" min="0" step="0.01" value={edit.default_fee}
                           onChange={(e) => setEdit((s) => ({ ...s, default_fee: e.target.value }))}
                           data-testid={`firm-services-edit-fee-${r.service_id}`}/>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <label className="text-[10px] inline-flex items-center gap-1">
                      <input type="checkbox" checked={!!edit.active}
                             onChange={(e) => setEdit((s) => ({ ...s, active: e.target.checked }))}/>
                      active
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={saveEdit} disabled={busy} className="btn-primary text-[11px] py-1 px-2"
                              data-testid={`firm-services-edit-save-${r.service_id}`}>
                        {busy ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>}
                      </button>
                      <button onClick={cancelEdit} className="btn-secondary text-[11px] py-1 px-2"><XIcon size={11}/></button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={r.service_id} className={`border-t border-[var(--border)] ${r.active === false ? "opacity-60" : ""}`}
                    data-testid={`firm-services-row-${r.service_id}`}>
                  <td className="px-3 py-2 font-mono text-[var(--ink-3)]">{r.sort_order || 0}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.name}</div>
                    {r.description && <div className="text-[11px] text-[var(--ink-3)] mt-0.5">{r.description}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[var(--ink-2)]">{r.code || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.default_fee != null ? `$${Number(r.default_fee).toLocaleString()}` : <span className="text-[var(--ink-3)]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.active === false
                      ? <span className="badge" style={{ background: "var(--ink-3)", color: "var(--paper)" }}>inactive</span>
                      : <span className="badge" style={{ background: "var(--moss)", color: "var(--paper)" }}>active</span>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => startEdit(r)} className="btn-secondary text-[11px] py-1 px-2"
                                data-testid={`firm-services-edit-btn-${r.service_id}`}>
                          Edit
                        </button>
                        {r.active === false
                          ? <button onClick={() => reactivate(r)} className="btn-secondary text-[11px] py-1 px-2"
                                    data-testid={`firm-services-reactivate-${r.service_id}`}><RotateCcw size={11}/></button>
                          : <button onClick={() => deactivate(r)} className="btn-secondary text-[11px] py-1 px-2 text-[#B91C1C] hover:!border-[#B91C1C]"
                                    data-testid={`firm-services-delete-${r.service_id}`}><Trash2 size={11}/></button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
